<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import UIKit from '@pocketprep/ui-kit'
import { monthsAbrev } from '@/utils'
import Loading from '@/components/Loading.vue'
import type { Teach } from '@pocketprep/types'
import { statsModule } from '@/store/stats/module'
import { analyticsModule } from '@/store/analytics/module'

const {
    Bar: PocketBar,
    SegmentControl,
} = UIKit

const displayedDataType = ref(1)

const currentStudentStats = computed(() => statsModule.getters.getCurrentStudentStats())

const quizScores = computed(() => {
    const unsortedQuizzes = displayedDataType.value === 1
        ? currentStudentStats.value.dayScores
        : currentStudentStats.value.quizScores
    return unsortedQuizzes.sort((a, b) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
    )
})

const footLabels = computed(() => {
    return (displayedDataType.value === 1 || quizScores.value.length === 1)
        ? {
            left: formatDateString(quizScores.value[0]?.date),
            right: formatDateString(quizScores.value[quizScores.value.length - 1]?.date),
        }
        : { left: 'Oldest', right: 'Most Recent' }
})

const formatDataForChart = (data: Teach.Cloud.StudentStats['dayScores']) => {
    return data.map(qs => ({
        y: qs.score,
        info: qs.date,
    }))
}

const formatDateString = (dateString: string | number | undefined) => {
    const date = new Date(String(dateString))

    return `${monthsAbrev[date.getMonth()]} ${date.getDate()}`
}

const changeOption = (option: number) => {
    displayedDataType.value = option

    const optionName = option === 1 ? 'by Day' : 'by Quiz'
    analyticsModule.actions.amplitudeTrack('Segment Control Clicked', {
        segmentControlContext: 'Student Stats Quiz Scores',
        segmentControlValue: optionName,
    })
}

onMounted(async () => {
    await statsModule.actions.fetchCurrentStudentStats()
})
</script>

<template>
    <div class="quiz-scores" :class="{ 'quiz-scores--loading': currentStudentStats.isLoading }">
        <SectionHead
            title="Quiz Scores"
            icon="barChart"
        >
            <SegmentControl
                v-if="quizScores.length"
                option1="by Day"
                option2="by Quiz"
                @change="changeOption($event)"
            />
        </SectionHead>
        <Loading v-if="currentStudentStats.isLoading" />
        <template v-else-if="quizScores.length">
            <PocketBar
                :key="JSON.stringify(quizScores)"
                :data="formatDataForChart(quizScores)"
                type="percent"
                size="small"
                class="quiz-scores__chart"
            />
            <div class="quiz-scores__foot">
                <div :class="{ 'quiz-scores__foot--center': footLabels.left === footLabels.right }">
                    {{ footLabels.left }}
                </div>
                <div v-if="footLabels.right !== footLabels.left">
                    {{ footLabels.right }}
                </div>
            </div>
        </template>
        <template v-else>
            <p>
                No quiz scores.
            </p>
        </template>
    </div>
</template>

<style lang="scss" scoped>


.quiz-scores {
    display: flex;
    flex-direction: column;
    min-height: 300px;

    .quiz-scores__chart {
        margin-top: 10px;
        height: 100%;
    }

    .quiz-scores__foot {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: $slate-01;
        font-size: 11px;
        letter-spacing: 0.28px;
        flex: 1;

        div:last-child {
            padding-right: 42px;
        }

        .quiz-scores__foot--center {
            width: 100%;
            text-align: center;
        }
    }

    &--loading {
        .loading {
            height: 300px;
        }

        .quiz-scores__chart {
            filter: blur(4px);
        }
    }
}
</style>
